import {
  ButtonMenu,
  CollapseToggle,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  InlineFeedbackBanner,
  MenuItem,
  NumberField,
  actionColumn,
  column,
  dragHandleColumn,
  emptyCell,
  insertAtIndex,
  numericColumn,
  simpleHeader,
  useComputed,
  useModal,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { camelCase } from "change-case";
import { ReactNode, useRef, useState } from "react";
import { BoundAiSelectField, BoundAiTextField } from "src/components/formFields/ai";
import { EntityProposal } from "src/components/formFields/ai/utils/aiProposalForm";
import { reconcilePendingAiProposals } from "src/components/formFields/ai/utils/reconcilePendingAiProposals";
import { AiEntityProposalType, AiFieldProposalStatus } from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  PlanPackageLocationsV2Query,
  PlanPackageLocationsV2_LocationFragment,
} from "src/routes/libraries/plan-package-v2/components/LocationsTab.generated";
import {
  FieldProposalLookup,
  RoomProposalsLookup,
  RoomWarning,
  fullRoomNumberBinding,
  roomProposalBindings,
} from "src/routes/libraries/plan-package-v2/components/locations/locationProposalUtils";
import {
  GetRoomFormState,
  LevelGroup,
  RoomFormValue,
  SaveRpls,
  activeOptionsCount,
  describeRoomRange,
  formatFullRoomNumber,
  permuteRoomNumbers,
  validateFullRoomNumber,
} from "src/routes/libraries/plan-package-v2/components/locations/locationUtils";

type LevelLocationsTableProps = {
  group: LevelGroup;
  roomTypes: PlanPackageLocationsV2Query["roomTypes"];
  getFormState: GetRoomFormState;
  disabled: boolean | ReactNode;
  saveRpls: SaveRpls;
  isDuplicateFullRoomNumber: (rplId: string, fullRoomNumber: number) => boolean;
  showLevelDelete: boolean;
  /** The AI's proposals, keyed by ReadyPlanLocation id. Empty when the flag is off. */
  roomProposals: Map<string, EntityProposal>;
  getFieldProposal: FieldProposalLookup;
  getRoomProposals: RoomProposalsLookup;
  warnings: RoomWarning[];
};

export function LevelLocationsTable(props: LevelLocationsTableProps) {
  const {
    group,
    roomTypes,
    getFormState,
    disabled,
    saveRpls,
    isDuplicateFullRoomNumber,
    showLevelDelete,
    roomProposals,
    getFieldProposal,
    getRoomProposals,
    warnings,
  } = props;
  const { openModal } = useModal();
  const levelName = group.location.name;
  const orderedRooms = useComputed(
    () => group.rooms.sortBy((rpl) => getFormState(rpl).roomNumber.value ?? 0),
    [group, getFormState],
  );
  const rows = useComputed<GridDataRow<Row>[]>(
    () => [
      simpleHeader,
      {
        kind: "level",
        id: group.location.id,
        data: group,
        children: orderedRooms.map((rpl) => ({
          kind: "room" as const,
          id: rpl.id,
          // FIXME: If we update beam to expose a `removeCompanionRow` cb or something this wont be needed
          // The warning rides along in `data` on purpose: `GridTable` is not an observer and
          // `RowState._row` is not observable, so a companion that merely changes identity is never
          // re-read. `_data` is `observable.ref` and is what `RowState.row` hangs its reactivity on,
          // so putting the warning here is what lets the banner clear.
          data: { rpl, warning: warnings.find((w) => w.rplId === rpl.id)?.message },
          draggable: !disabled,
          aiMode: () => getRoomProposals(rpl.id).some((p) => p.status.value === AiFieldProposalStatus.Pending),
          companion: companionFor(warnings.find((w) => w.rplId === rpl.id)?.message),
        })),
      },
    ],
    [group, orderedRooms, disabled, warnings],
  );

  /**
   * A room proposed for removal renders its cells as struck-through static text.
   *
   * FIXME: Either design signs off on this or we update the beam component.
   *
   * TEMPORARY — this belongs in the fields themselves. The Figma's treatment is exactly what
   * `TextFieldBase` already draws for `originalValue` with no `proposedValue`, i.e. what you'd expect
   * `{ originalValue: "Dining Room", proposedValue: undefined }` to do. Two things in Beam stop that
   * working today, and we own Beam, so both are fixable rather than facts of life:
   *
   *  1. `originalValue` isn't on `TextFieldProps`/`BeamTextFieldProps` — only `proposedValue` is. It
   *     exists one layer down on `TextFieldBase`.
   *  2. `TextField` spreads `{...proposalProps}` last, and `useAiProposal` sets `originalValue:
   *     undefined` whenever there's no `proposedValue`, so a caller-supplied value is clobbered.
   *
   * Fix Beam (expose `originalValue`, and don't let `proposalProps` overwrite a caller's) and this
   * helper and its three `isProposedForRemoval` branches should go away in favour of passing the
   * struck original straight to `BoundAiSelectField` / `BoundAiTextField` / the Order cell. Same
   * family as the `companion` reactivity gap — the AI props are wired for "there is a proposal" and
   * don't compose for "there was a value and now there won't be".
   *
   */
  function isProposedForRemoval(rplId: string): boolean {
    return roomProposals.get(rplId)?.type === AiEntityProposalType.Delete;
  }

  function struck(content: ReactNode) {
    return <span css={Css.tdlt.gray700.$}>{content}</span>;
  }

  function companionFor(message: string | undefined) {
    return message ? () => <InlineFeedbackBanner type="warning" description={message} /> : undefined;
  }

  function onRowDrop(dragged: GridDataRow<Row>, dropped: GridDataRow<Row>, indexOffset: number) {
    // Rooms can only be reordered against other rooms, and dropping a row onto itself is a noop
    if (dragged.kind !== "room" || dropped.kind !== "room" || dragged.id === dropped.id) return;
    const currentIds = orderedRooms.map((rpl) => rpl.id);
    if (!currentIds.includes(dragged.id) || !currentIds.includes(dropped.id)) return;
    const withoutDragged = currentIds.filter((id) => id !== dragged.id);
    const newOrderIds = insertAtIndex(withoutDragged, dragged.id, withoutDragged.indexOf(dropped.id) + indexOffset);
    // Reassign the level's existing room numbers to the new visual order, keeping only the rooms that changed
    const changes = permuteRoomNumbers(
      orderedRooms.map((rpl) => ({ id: rpl.id, roomNumber: getFormState(rpl).roomNumber.value ?? 0 })),
      newOrderIds,
    );
    if (changes.isEmpty) return;
    const handleSave = () => saveRpls(changes);
    const changedRpls = changes.map((change) => orderedRooms.find((rpl) => rpl.id === change.id)!);
    // Renumbering cascades to attached TLIs and options, so get confirmation first
    if (changedRpls.some((rpl) => rpl.relatedTakeoffLineItemsCount > 0 || activeOptionsCount(rpl) > 0)) {
      const fullRoomNumbers = changedRpls.map((rpl) => rpl.fullRoomNumber!);
      openModal({
        content: (
          <ConfirmationModal
            title="Update Room Numbers"
            label="Update"
            confirmationMessage={updateRoomNumbersMessage(describeRoomRange(levelName, fullRoomNumbers))}
            onConfirmAction={handleSave}
          />
        ),
      });
    } else {
      void handleSave();
    }
  }

  function openDeleteRoomModal(rpl: PlanPackageLocationsV2_LocationFragment) {
    openModal({
      content: (
        <ConfirmationModal
          title="Delete Room"
          label="Delete"
          danger
          confirmationMessage={
            <div css={Css.df.fdc.gap2.$}>
              <p>Are you sure you want to delete this location?</p>
              <p>
                Deleting this location will remove it from {rpl.relatedTakeoffLineItemsCount} takeoff lines and{" "}
                {activeOptionsCount(rpl)} options.
              </p>
            </div>
          }
          onConfirmAction={() => saveRpls([{ id: rpl.id, delete: true }], { membershipChanged: true })}
        />
      ),
    });
  }

  function openDeleteLevelModal() {
    openModal({
      content: (
        <ConfirmationModal
          title="Delete Level"
          label="Delete"
          danger
          confirmationMessage="This level, all its rooms, and all associated items in the Takeoff will be removed."
          onConfirmAction={() => saveRpls([{ id: group.levelRpl!.id, delete: true }], { membershipChanged: true })}
        />
      ),
    });
  }

  /**
   * AI actions come first and carry the sparkle, so they read as AI affordances.
   *
   * TODO: Beam's `MenuItem` needs an `aiMode` prop to auto insert the aiStar and color the label.
   */
  function roomMenuItems(rpl: PlanPackageLocationsV2_LocationFragment): MenuItem[] {
    const os = getFormState(rpl);
    const reconcile = (decision: AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected) => () =>
      reconcilePendingAiProposals(
        roomProposalBindings(os, rpl.id, levelName, getFieldProposal),
        decision,
        getRoomProposals(rpl.id),
      );
    return [
      { label: "Accept Changes", icon: "aiStar", onClick: reconcile(AiFieldProposalStatus.Accepted) },
      { label: "Ignore Change", icon: "aiStar", onClick: reconcile(AiFieldProposalStatus.Rejected) },
      {
        label: "View Import Source",
        icon: "aiStar",
        // TODO: PLAN-XXXX — jump to the evidence page in the source document. AiFieldProposal has no
        // page-number column; the agent payload returns `page_numbers` per field but we don't persist it.
        onClick: () => alert("View Import Source coming soon"),
      },
      {
        label: "Remove Room",
        onClick: () => openDeleteRoomModal(rpl),
        disabled: !!disabled || disableBasedOnPotentialOperation(rpl.canDelete),
      },
    ];
  }

  const columns: GridColumn<Row>[] = [
    dragHandleColumn<Row>({
      header: { content: "Order", colspan: 2, alignment: "left" },
      level: (_, { row }) => ({ content: () => <CollapseToggle row={row} compact /> }),
    }),
    numericColumn<Row>({
      header: emptyCell,
      level: () => ({ content: () => <span css={Css.smSb.$}>{levelName}</span>, colspan: 3, alignment: "left" }),
      room: ({ rpl }) => ({
        content: () =>
          isProposedForRemoval(rpl.id) ? (
            struck(rpl.fullRoomNumber)
          ) : (
            <OrderCell
              rpl={rpl}
              os={getFormState(rpl)}
              levelName={levelName}
              disabled={disabled}
              isDuplicateFullRoomNumber={isDuplicateFullRoomNumber}
              getFieldProposal={getFieldProposal}
            />
          ),
      }),
      w: "80px",
    }),
    column<Row>({
      header: "Room",
      level: emptyCell,
      room: ({ rpl }) =>
        isProposedForRemoval(rpl.id) ? (
          struck(roomTypes.find((rt) => rt.id === rpl.roomType?.id)?.name ?? rpl.location.name)
        ) : (
          <BoundAiSelectField
            field={getFormState(rpl).roomTypeId}
            label="Room"
            labelStyle="hidden"
            options={roomTypes}
            unsetLabel={rpl.location.name}
            disabled={disabled}
            aiProposal={getFieldProposal(rpl.id, "roomTypeId")}
          />
        ),
      // TODO: This column must be will have
      w: 2,
    }),
    column<Row>({
      header: "Marketing Name",
      level: emptyCell,
      room: ({ rpl }) =>
        isProposedForRemoval(rpl.id) ? (
          struck(rpl.marketingName)
        ) : (
          <BoundAiTextField
            field={getFormState(rpl).marketingName}
            label="Marketing Name"
            labelStyle="hidden"
            disabled={disabled}
            aiProposal={getFieldProposal(rpl.id, "marketingName")}
          />
        ),
    }),
    actionColumn<Row>({
      header: emptyCell,
      level: () =>
        showLevelDelete ? (
          <IconButton icon="trash" label="Delete Level" onClick={openDeleteLevelModal} disabled={disabled} />
        ) : (
          emptyCell
        ),
      room: ({ rpl }) =>
        roomProposals.has(rpl.id) ? (
          <ButtonMenu trigger={{ icon: "verticalDots" }} placement="right" items={roomMenuItems(rpl)} />
        ) : (
          <IconButton
            icon="trash"
            onClick={() => openDeleteRoomModal(rpl)}
            disabled={disabled || disableBasedOnPotentialOperation(rpl.canDelete)}
          />
        ),
      w: "56px",
    }),
  ];

  return (
    <GridTable
      id={`${camelCase(levelName, { mergeAmbiguousCharacters: true })}Table`} // "Level 1" -> level1Table
      columns={columns}
      rows={rows}
      onRowDrop={onRowDrop}
      style={{ bordered: true, allWhite: true, inlineEditing: true, rowHeight: "fixed" }}
    />
  );
}

type HeaderRow = { kind: "header"; id: string; data: undefined };
type LevelRow = { kind: "level"; id: string; data: LevelGroup };
/** The room plus its derived warning, so a changed warning changes the row's `data` identity. */
type RoomRowData = { rpl: PlanPackageLocationsV2_LocationFragment; warning: string | undefined };
type RoomRow = { kind: "room"; id: string; data: RoomRowData };
type Row = HeaderRow | LevelRow | RoomRow;

type OrderCellProps = {
  rpl: PlanPackageLocationsV2_LocationFragment;
  os: ObjectState<RoomFormValue>;
  levelName: string;
  disabled: boolean | ReactNode;
  isDuplicateFullRoomNumber: (rplId: string, fullRoomNumber: number) => boolean;
  getFieldProposal: FieldProposalLookup;
};

/**
 * An unbound field over `os.roomNumber` so typed values only commit on blur, after validation and
 * (when takeoff lines or options are attached) the Update Room Numbers confirmation.
 *
 * Stays unbound rather than becoming a `BoundAiNumberField` because that confirmation has to run
 * before the value lands; the AI proposal is wired in by hand instead.
 */
function OrderCell(props: OrderCellProps) {
  const { rpl, os, levelName, disabled, isDuplicateFullRoomNumber, getFieldProposal } = props;
  const { openModal } = useModal();
  const [pending, setPending] = useState<number>();
  // Mirrors `pending` for commit() to read: blur can fire before the state update has rendered (e.g. paste, then tab)
  const pendingRef = useRef<number>();
  const [errorMsg, setErrorMsg] = useState<string>();
  const savedFullRoomNumber = useComputed(
    () => (os.roomNumber.value ? formatFullRoomNumber(levelName, os.roomNumber.value) : undefined),
    [os, levelName],
  );
  // The proposal holds the full room number the AI read off the drawing, which is what this cell shows.
  const proposedFullRoomNumber = useComputed(() => {
    const proposal = getFieldProposal(rpl.id, "roomNumber");
    if (proposal?.status.value !== AiFieldProposalStatus.Pending) return undefined;
    const parsed = Number(proposal.fieldValue.value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [os, rpl]);

  function commit() {
    const typed = pendingRef.current;
    if (typed === undefined) return;
    const validated = validateFullRoomNumber(levelName, typed);
    if ("error" in validated) return setErrorMsg(validated.error);
    if (isDuplicateFullRoomNumber(rpl.id, typed)) return setErrorMsg("Duplicate room number");
    pendingRef.current = undefined;
    setPending(undefined);
    // Editing the cell resolves its proposal either way — matching the typed value accepts it, anything
    // else rejects it — so this has to run even when the value itself is unchanged.
    const binding = fullRoomNumberBinding(os, rpl.id, levelName, getFieldProposal);
    binding.proposal?.status.set(
      binding.parse(binding.proposal.fieldValue.value) === validated.roomNumber
        ? AiFieldProposalStatus.Accepted
        : AiFieldProposalStatus.Rejected,
    );
    if (validated.roomNumber === os.roomNumber.value) return;
    const apply = () => os.roomNumber.set(validated.roomNumber);
    if (rpl.relatedTakeoffLineItemsCount > 0 || activeOptionsCount(rpl) > 0) {
      openModal({
        content: (
          <ConfirmationModal
            title="Update Room Numbers"
            label="Update"
            confirmationMessage={updateRoomNumbersMessage(describeRoomRange(levelName, [savedFullRoomNumber!]))}
            onConfirmAction={apply}
          />
        ),
      });
    } else {
      apply();
    }
  }

  return (
    <NumberField
      label="Order"
      labelStyle="hidden"
      value={pending ?? savedFullRoomNumber}
      proposedValue={proposedFullRoomNumber}
      onChange={(value) => {
        pendingRef.current = value;
        setPending(value);
        setErrorMsg(undefined);
      }}
      onBlur={commit}
      errorMsg={errorMsg}
      errorInTooltip
      positiveOnly
      disabled={disabled}
    />
  );
}

function updateRoomNumbersMessage(roomRange: string) {
  return (
    <div css={Css.df.fdc.gap2.$}>
      <p>
        Are you sure you want to update the room numbers for {roomRange}? Be sure these room numbers match the latest
        construction documents.
      </p>
      <p>
        Options and takeoff lines assigned to these room numbers will also be updated to reflect the updated room
        numbers.
      </p>
    </div>
  );
}
